import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import { AlbumFolderWithCreatorBrandDetails } from '../../../graphql/queries/album-folder';
import { MeWithIdentitiesAndSubscribed } from '../../../graphql/queries/account';
import {
  albumFolderSubscriptionRequired,
  isAlbumFolderTagSelectionRequiredForCheckout,
} from '../../../util/album-folder';
import Spinner from '../../global/Spinner';

/**
 * Higher order component for the require purchased album folder functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {React.Component} The composed component.
 */
const WithRequirePurchasedAlbumFolder = WrappedComponent =>
  class WithRequirePurchasedAlbumFolderClass extends Component {
    static propTypes = {
      rootAlbumFolder: PropTypes.shape({
        id: PropTypes.string.isRequired,
        album_subtype: PropTypes.string,
      }),
      me: PropTypes.shape({
        linked_identities: PropTypes.shape({
          records: PropTypes.arrayOf(
            PropTypes.shape({
              purchased_album_folders: PropTypes.shape({
                records: PropTypes.arrayOf(
                  PropTypes.shape({
                    id: PropTypes.string,
                  }),
                ),
              }),
            }),
          ),
        }),
      }),
    };

    render() {
      const { rootAlbumFolder, me } = this.props;

      if (!rootAlbumFolder || !me) {
        return (
          <div className="wrapper-main min-h-100vh justify-content-center align-items-center">
            <Spinner />
          </div>
        );
      }

      const identities = me?.linked_identities?.records ?? [];
      const subscribedIdentities =
        me?.subscribed_album_folder_identities?.records ?? [];

      const isPurchased = identities.some(identity => {
        return identity.purchased_album_folders.records.some(
          folder => folder.id === rootAlbumFolder.id,
        );
      });

      const isSubscribed = (subscribedIdentities || []).find(
        subscribedItem =>
          subscribedItem?.album_folder?.id === rootAlbumFolder?.id,
      );

      return !isPurchased &&
        !isSubscribed &&
        albumFolderSubscriptionRequired(rootAlbumFolder) &&
        !isAlbumFolderTagSelectionRequiredForCheckout(rootAlbumFolder) ? (
        <Redirect
          to={`/purchase/album-folder/${rootAlbumFolder.id}/purchase-subscription`}
        />
      ) : (
        <WrappedComponent {...this.props} />
      );
    }
  };

/**
 * Higher order container for the require valid album folder functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const RequirePurchasedAlbumFolder = WrappedComponent => {
  const WithPurchasedAlbumFolder =
    WithRequirePurchasedAlbumFolder(WrappedComponent);

  const withAlbumFolderData = graphql(AlbumFolderWithCreatorBrandDetails, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumFolderId,
      },
      notifyOnNetworkStatusChange: true,
    }),
    props: ({ data: { albumFolder, loading } = {} }) => ({
      rootAlbumFolder: albumFolder?.root,
      loading,
    }),
  });

  const withPurchasedAlbumFolders = graphql(MeWithIdentitiesAndSubscribed, {
    options: ({ match }) => ({
      notifyOnNetworkStatusChange: true,
      // This *needs* `network-only` as when completing onboarding
      // the UI needs to know which identities are purchased
      // in order to proceed with after-onboarding views (i.e. Activities/Tags).
      fetchPolicy: 'network-only',
      variables: {
        albumFolderIds: [match.params.albumFolderId],
      },
    }),
    props: ({ data: { me, loading } }) => ({
      me,
      loading,
    }),
  });

  return compose(
    withAlbumFolderData,
    withPurchasedAlbumFolders,
  )(WithPurchasedAlbumFolder);
};

export { WithRequirePurchasedAlbumFolder };
export default RequirePurchasedAlbumFolder;
